import React, { useState } from 'react';
import { FiSearch, FiTrendingUp } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`/TopUniversitiesandColleges?search=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleTrendingClick = (tag) => {
        navigate(`/TopUniversitiesandCollegesTag_${tag}`);
    };

    return (
        <section className="relative min-h-[85vh] overflow-hidden flex items-center pt-8 pb-12 -mt-[20px] lg:pt-10 lg:-mt-[30px]">
            {/* Main Background Image */}
            <div className="absolute inset-0 z-0">
                <img
                    src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop"
                    alt="University Campus"
                    className="w-full h-full object-cover"
                />
                {/* Dark Gradient Overlay for Readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-purple-900/90 to-slate-900/80 mix-blend-multiply"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>

            {/* Content Container */}
            <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">

                {/* Left Content */}
                <div className="text-white space-y-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-block bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide mb-2 border border-white/20 text-blue-100 shadow-lg"
                    >
                        <FiTrendingUp className="inline mr-2 text-orange-400" />
                        <span className="text-orange-50 uppercase tracking-wider">#1 Education Portal</span>
                    </motion.div>

                    <h1 className="text-5xl lh-30 lg:text-6xl font-extrabold leading-tight">
                        Find The Best <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400">College & Universities</span> <br />
                        For Your Career
                    </h1>

                    <p className="text-lg text-gray-200 max-w-lg leading-relaxed font-medium">
                        Explore thousands of colleges, courses, and scholarships with our AI-powered recommendation engine. Your future starts here.
                    </p>

                    {/* Search Bar */}
                    <div className="bg-white/95 backdrop-blur-sm p-2 rounded-xl shadow-2xl w-full max-w-xl flex items-center gap-2 transform transition-transform hover:scale-[1.01] border border-white/10 text-gray-800">
                        <FiSearch className="text-gray-500 ml-3 mr-2 flex-shrink-0" size={24} />
                        <input
                            type="text"
                            placeholder="Search for colleges, exams..."
                            className="flex-grow outline-none text-gray-800 px-2 py-3 bg-transparent text-base font-medium placeholder-gray-500 min-w-0"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                        />
                        <button
                            onClick={handleSearch}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 sm:px-8 py-3 rounded-lg font-bold hover:shadow-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex-shrink-0 whitespace-nowrap"
                        >
                            Search
                        </button>
                    </div>

                    {/* Trending Tags */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-200">
                        <span className="font-medium text-gray-300">Trending:</span>
                        {['Engineering', 'MBA', 'Medical', 'Study Abroad'].map((tag, index) => (
                            <motion.button
                                key={tag}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 + index * 0.1 }}
                                onClick={() => handleTrendingClick(tag)}
                                className="border border-white/20 bg-white/10 px-4 py-1.5 rounded-full hover:bg-white/20 hover:border-white/40 cursor-pointer transition-all duration-300 focus:outline-none backdrop-blur-md text-white shadow-sm"
                            >
                                {tag}
                            </motion.button>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-8 pt-8 border-t border-white/20 max-w-lg">
                        <div>
                            <h3 className="text-3xl font-bold text-white">5k+</h3>
                            <p className="text-xs text-gray-300 uppercase mt-1 font-medium tracking-wider">Colleges</p>
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold text-white">500+</h3>
                            <p className="text-xs text-gray-300 uppercase mt-1 font-medium tracking-wider">Exams</p>
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold text-white">1M+</h3>
                            <p className="text-xs text-gray-300 uppercase mt-1 font-medium tracking-wider">Students</p>
                        </div>
                    </div>
                </div>

                {/* Right Content - Floating 3D Elements or Image */}
                <div className="hidden lg:block relative perspective-1000">
                    {/* Main Image Container with Glow */}
                    <motion.div
                        initial={{ opacity: 0, x: 50, rotateY: -10 }}
                        animate={{ opacity: 1, x: 0, rotateY: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mb-20 relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 transform hover:scale-[1.02] transition-transform duration-500 group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/10 to-purple-500/10 mix-blend-overlay z-10 pointer-events-none"></div>
                        {/* Swapped to a reliable Image URL */}
                        <img
                            src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?q=80&w=2070&auto=format&fit=crop"
                            alt="Student Success"
                            className="w-full h-auto object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />

                        {/* Floating Card - Top Right */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                            className="absolute -right-8 top-14 bg-white/95 backdrop-blur-md px-5 pr-4 py-4 rounded-xl shadow-xl z-20 animate-float border border-white/50"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path></svg>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Top Ranked</p>
                                    <p className="text-sm font-bold text-gray-800">#1 Institute</p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Floating Card - Bottom Left */}
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.8 }}
                            className="absolute -left-6 bottom-12 bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-xl z-20 animate-float-delayed border border-white/50"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-orange-100 p-2 rounded-full text-orange-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Fast Track</p>
                                    <p className="text-sm font-bold text-gray-800">Admissions</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Background Glow behind Image */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-3xl blur-2xl opacity-40 -z-10 animate-pulse"></div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
